/**
 * View preferences that persist across designs and sessions.
 *
 * Whether labels, dimensions, fluid lines, air lines and notes are drawn, and how the
 * grid snaps, is how this person likes to look at
 * a schematic, not a property of the design, so it is kept in the browser
 * rather than in the draft: it never reaches the server, never changes a
 * design, and one designer's choice does not change what a colleague sees.
 *
 * Anything unreadable (a stored value from an older shape, storage disabled
 * or full) falls back to the defaults rather than failing the editor.
 */
import { NO_DEFAULTS, type PlacementDefaults } from "../editor/defaults";
import { DEFAULT_GRID, type GridSettings } from "../editor/state";
import { ALL_ANNOTATIONS, type SceneAnnotations } from "../scene/types";

export const ANNOTATIONS_KEY = "fluidea.view.annotations";

// The project was called Cascadia before D47 and its keys carried that prefix.
// A preference stored under the old name moves to the new one the first time it
// is read, so nobody's settings reset with the rename. Idempotent: once the old
// keys are gone this is a handful of misses.
const LEGACY_PREFIX = "cascadia.";
const CURRENT_PREFIX = "fluidea.";

function adoptLegacyKeys(store: Storage): void {
  const keys = [
    ANNOTATIONS_KEY,
    GRID_KEY,
    INSPECTOR_KEY,
    PALETTE_SECTIONS_KEY,
    PALETTE_KEY,
    PLACEMENT_DEFAULTS_KEY,
  ];
  for (const key of keys) {
    const legacy = LEGACY_PREFIX + key.slice(CURRENT_PREFIX.length);
    const value = store.getItem(legacy);
    if (value === null) continue;
    if (store.getItem(key) === null) store.setItem(key, value);
    store.removeItem(legacy);
  }
}

function storage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    const store = window.localStorage;
    try {
      adoptLegacyKeys(store);
    } catch {
      // Full storage: the old keys stay where they are and are tried again next time.
    }
    return store;
  } catch {
    // Some privacy modes throw on access.
    return null;
  }
}

export function loadAnnotations(): SceneAnnotations {
  try {
    const raw = storage()?.getItem(ANNOTATIONS_KEY);
    if (!raw) return ALL_ANNOTATIONS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return ALL_ANNOTATIONS;
    const stored = parsed as Record<string, unknown>;
    // Each setting on its own: one stored before a setting existed keeps the rest.
    const flag = (key: "labels" | "dimensions" | "fluidLines" | "airLines" | "notes") =>
      typeof stored[key] === "boolean" ? (stored[key] as boolean) : ALL_ANNOTATIONS[key];
    return {
      labels: flag("labels"),
      dimensions: flag("dimensions"),
      fluidLines: flag("fluidLines"),
      airLines: flag("airLines"),
      notes: flag("notes"),
      // Never kept: a design opens without a wash.
      wash: "none",
    };
  } catch {
    return ALL_ANNOTATIONS;
  }
}

export function saveAnnotations(annotations: SceneAnnotations): void {
  try {
    storage()?.setItem(
      ANNOTATIONS_KEY,
      JSON.stringify({
        labels: annotations.labels,
        dimensions: annotations.dimensions,
        fluidLines: annotations.fluidLines,
        airLines: annotations.airLines,
        notes: annotations.notes,
      }),
    );
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

// --- The grid ------------------------------------------------------------------

export const GRID_KEY = "fluidea.editor.grid";

/** Grid spacing the settings page accepts, in scene units. */
export const GRID_SPACING = { minimum: 4, maximum: 64 } as const;

/**
 * Snapping and spacing: how this person likes to draw, set on the settings
 * page and applied to every design, like the view toggles. Never saved to a
 * design: the grid is presentation (invariant 4).
 */
export function loadGrid(): GridSettings {
  try {
    const raw = storage()?.getItem(GRID_KEY);
    if (!raw) return DEFAULT_GRID;
    const stored = JSON.parse(raw) as Record<string, unknown>;
    const spacing = stored.spacing;
    return {
      spacing:
        typeof spacing === "number" && spacing >= GRID_SPACING.minimum && spacing <= GRID_SPACING.maximum
          ? spacing
          : DEFAULT_GRID.spacing,
      snap: typeof stored.snap === "boolean" ? stored.snap : DEFAULT_GRID.snap,
      visible: typeof stored.visible === "boolean" ? stored.visible : DEFAULT_GRID.visible,
    };
  } catch {
    return DEFAULT_GRID;
  }
}

export function saveGrid(grid: GridSettings): void {
  try {
    storage()?.setItem(GRID_KEY, JSON.stringify({ spacing: grid.spacing, snap: grid.snap, visible: grid.visible }));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

// --- The workspace's panels ------------------------------------------------------

export const INSPECTOR_KEY = "fluidea.workspace.inspector";
export const PALETTE_SECTIONS_KEY = "fluidea.workspace.palette";

/**
 * Every side pane that folds away to its tab: each step's left pane, and the
 * right pane however it reads (inspector or scripts editor, which is one pane
 * in two forms). The palette keeps its own key because its width travels with
 * its open flag.
 */
export const PANE_OPEN_KEYS = {
  checks: "fluidea.workspace.checks-pane",
  scripts: "fluidea.workspace.scripts-pane",
  inspector: INSPECTOR_KEY,
  definition: "fluidea.workspace.definition-pane",
} as const;

export type FoldablePane = keyof typeof PANE_OPEN_KEYS;

/** Whether this pane is open beside the canvas. Open unless this person folded it away. */
export function loadPaneOpen(pane: FoldablePane): boolean {
  try {
    return storage()?.getItem(PANE_OPEN_KEYS[pane]) !== "closed";
  } catch {
    return true;
  }
}

export function savePaneOpen(pane: FoldablePane, open: boolean): void {
  try {
    storage()?.setItem(PANE_OPEN_KEYS[pane], open ? "open" : "closed");
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

/**
 * The palette sections this person has open, by label. Null when nothing is
 * stored, so the palette can choose its own first view.
 */
export function loadPaletteSections(): string[] | null {
  try {
    const raw = storage()?.getItem(PALETTE_SECTIONS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((label): label is string => typeof label === "string") : null;
  } catch {
    return null;
  }
}

export function savePaletteSections(labels: readonly string[]): void {
  try {
    storage()?.setItem(PALETTE_SECTIONS_KEY, JSON.stringify(labels));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

export const PALETTE_KEY = "fluidea.workspace.palette-panel";

/** How wide the palette may be dragged, in CSS pixels. */
export const PALETTE_WIDTH = { minimum: 140, maximum: 480, default: 184 } as const;

export interface PalettePanel {
  open: boolean;
  width: number;
}

/** Whether the palette is open, and how wide this person made it. */
export function loadPalettePanel(): PalettePanel {
  const fallback = { open: true, width: PALETTE_WIDTH.default };
  try {
    const raw = storage()?.getItem(PALETTE_KEY);
    if (!raw) return fallback;
    const stored = JSON.parse(raw) as Record<string, unknown>;
    const width = stored.width;
    return {
      open: typeof stored.open === "boolean" ? stored.open : fallback.open,
      width:
        typeof width === "number" && width >= PALETTE_WIDTH.minimum && width <= PALETTE_WIDTH.maximum
          ? width
          : fallback.width,
    };
  } catch {
    return fallback;
  }
}

export function savePalettePanel(panel: PalettePanel): void {
  try {
    storage()?.setItem(PALETTE_KEY, JSON.stringify({ open: panel.open, width: panel.width }));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

// --- Side pane widths ------------------------------------------------------------

export const PANE_WIDTHS_KEY = "fluidea.workspace.pane-widths";

/** How wide a pane may be dragged, in CSS pixels, and where it starts. */
export interface PaneLimits {
  minimum: number;
  maximum: number;
  default: number;
}

/**
 * The side panes besides the palette, which keeps its own key: each step's
 * left pane, and the right pane in the two forms it takes. The Scripts
 * editor is kept apart from the inspector because a step's operations want
 * more room than a component's parameters.
 */
export const PANES = {
  checks: { minimum: 220, maximum: 640, default: 320 },
  scripts: { minimum: 240, maximum: 900, default: 340 },
  inspector: { minimum: 220, maximum: 640, default: 300 },
  "scripts-editor": { minimum: 240, maximum: 720, default: 300 },
} as const satisfies Record<string, PaneLimits>;

export type Pane = keyof typeof PANES;

/** Every pane's width as this person left it; a stored width out of range is the default. */
export function loadPaneWidths(): Record<Pane, number> {
  const widths = Object.fromEntries(Object.entries(PANES).map(([pane, limits]) => [pane, limits.default])) as Record<Pane, number>;
  try {
    const raw = storage()?.getItem(PANE_WIDTHS_KEY);
    if (!raw) return widths;
    const stored = JSON.parse(raw) as Record<string, unknown>;
    for (const pane of Object.keys(PANES) as Pane[]) {
      const width = stored[pane];
      const limits = PANES[pane];
      if (typeof width === "number" && width >= limits.minimum && width <= limits.maximum) widths[pane] = width;
    }
    return widths;
  } catch {
    return widths;
  }
}

export function savePaneWidths(widths: Readonly<Record<Pane, number>>): void {
  try {
    storage()?.setItem(PANE_WIDTHS_KEY, JSON.stringify(widths));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

// --- Table column widths ----------------------------------------------------------

export const COLUMN_WIDTHS_KEY = "fluidea.workspace.columns";

/**
 * A table's column widths as this person left them, as fractions of the
 * table, so they fit however wide its pane is; null where none are stored or
 * what is stored does not fit `count` columns.
 */
export function loadColumnWidths(table: string, count: number): number[] | null {
  try {
    const raw = storage()?.getItem(COLUMN_WIDTHS_KEY);
    const stored = raw ? (JSON.parse(raw) as Record<string, unknown>)[table] : undefined;
    if (!Array.isArray(stored) || stored.length !== count) return null;
    if (!stored.every((width) => typeof width === "number" && width > 0 && width < 1)) return null;
    const total = (stored as number[]).reduce((sum, width) => sum + width, 0);
    return Math.abs(total - 1) < 0.01 ? (stored as number[]) : null;
  } catch {
    return null;
  }
}

export function saveColumnWidths(table: string, widths: readonly number[] | null): void {
  try {
    const store = storage();
    if (!store) return;
    const raw = store.getItem(COLUMN_WIDTHS_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    if (widths) all[table] = widths;
    else delete all[table];
    store.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(all));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

// --- The script's step table ---------------------------------------------------------

export const STEP_TABLE_KEY = "fluidea.workspace.step-table";

/**
 * The step table's columns, in CSS pixels: the component names, and one
 * width shared by every step, so the steps stay alike whichever is dragged.
 */
export const STEP_TABLE = {
  name: { minimum: 64, maximum: 360, default: 144 },
  step: { minimum: 36, maximum: 320, default: 84 },
} as const satisfies Record<string, PaneLimits>;

export type StepTableWidths = Record<keyof typeof STEP_TABLE, number>;

export const STEP_TABLE_DEFAULTS: StepTableWidths = { name: STEP_TABLE.name.default, step: STEP_TABLE.step.default };

/** Held within its limits. */
export function clampStepTable(column: keyof typeof STEP_TABLE, width: number): number {
  const limits = STEP_TABLE[column];
  return Math.round(Math.max(limits.minimum, Math.min(limits.maximum, width)));
}

/** The step table's widths as this person left them; a stored width out of range is the default. */
export function loadStepTableWidths(): StepTableWidths {
  const widths = { ...STEP_TABLE_DEFAULTS };
  try {
    const raw = storage()?.getItem(STEP_TABLE_KEY);
    if (!raw) return widths;
    const stored = JSON.parse(raw) as Record<string, unknown>;
    for (const column of Object.keys(STEP_TABLE) as (keyof typeof STEP_TABLE)[]) {
      const width = stored[column];
      const limits = STEP_TABLE[column];
      if (typeof width === "number" && width >= limits.minimum && width <= limits.maximum) widths[column] = width;
    }
    return widths;
  } catch {
    return widths;
  }
}

export function saveStepTableWidths(widths: StepTableWidths): void {
  try {
    storage()?.setItem(STEP_TABLE_KEY, JSON.stringify(widths));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

// --- Folded sections ----------------------------------------------------------------

export const FOLDS_KEY = "fluidea.workspace.folds";

/** Which folding sections this person left open or shut, by section; unknown where never touched. */
export function loadFolds(): Record<string, boolean> {
  try {
    const raw = storage()?.getItem(FOLDS_KEY);
    const stored = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    return Object.fromEntries(Object.entries(stored).filter(([, open]) => typeof open === "boolean")) as Record<string, boolean>;
  } catch {
    return {};
  }
}

export function saveFold(id: string, open: boolean): void {
  try {
    storage()?.setItem(FOLDS_KEY, JSON.stringify({ ...loadFolds(), [id]: open }));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}

// --- Placement defaults (D36) ------------------------------------------------------

export const PLACEMENT_DEFAULTS_KEY = "fluidea.placement.defaults";

const isDefaultValue = (value: unknown) =>
  typeof value === "boolean" || typeof value === "string" || (typeof value === "number" && Number.isFinite(value));

/**
 * This person's placement defaults: the values new components and lines start
 * with in any design that does not set its own. Only the shape is checked here;
 * each value is checked against its template's declaration where it is used.
 */
export function loadUserDefaults(): PlacementDefaults {
  try {
    const raw = storage()?.getItem(PLACEMENT_DEFAULTS_KEY);
    if (!raw) return NO_DEFAULTS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return NO_DEFAULTS;
    const defaults: Record<string, Record<string, unknown>> = {};
    for (const [entity, values] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof values !== "object" || values === null || Array.isArray(values)) continue;
      const kept = Object.fromEntries(Object.entries(values).filter(([, value]) => isDefaultValue(value)));
      if (Object.keys(kept).length > 0) defaults[entity] = kept;
    }
    return defaults;
  } catch {
    return NO_DEFAULTS;
  }
}

export function saveUserDefaults(defaults: PlacementDefaults): void {
  try {
    storage()?.setItem(PLACEMENT_DEFAULTS_KEY, JSON.stringify(defaults));
  } catch {
    // Full or disabled storage: the preference simply does not persist.
  }
}
